// Expression result-type inference (Item 2 / slices 3b–3d).
//
// Given the *text* of a non-column, non-literal operand (a function call, a
// CAST, a `::` cast, or arithmetic / concatenation), infer the coarse
// `OperandLiteralSqlType` of its result so downstream consumers can route
// operator suggestions (and, later, type-mismatch diagnostics) correctly.
//
// Two consumers share this:
//   - SR-RO `classifyOperand` (parseComparison) — populates `resultSqlType`
//     on the expression operand so the parsed Comparison carries result types.
//   - IntelliSense `findPrecedingExpressionType` — infers the type of the
//     expression immediately left of the cursor to prune type-invalid
//     operators (mirrors the column path).
//
// CONSERVATIVE BY DESIGN. We only assert a concrete category when it is
// unambiguous across the operand's engine; anything polymorphic (MIN / MAX /
// COALESCE / NULLIF / CASE), date arithmetic (`date + 1`), or engine-
// overloaded (`+` as MSSQL string concat, `||` as MySQL logical-OR) falls
// through to 'unknown' → the GENERIC operator lattice, which suppresses
// nothing. A wrong assertion would hide valid operators, so when in doubt we
// return 'unknown'.

import type { SqlEngine } from '../../sql-grammar/model';
import { columnSqlTypeFromDataType } from '../json/operators/columnSqlType';
import type { OperandLiteralSqlType } from '../types';

// Curated built-in functions whose result category is the SAME regardless of
// argument types, keyed by upper-cased name. Polymorphic functions (MIN, MAX,
// COALESCE, NULLIF, IFNULL, ISNULL, NVL, GREATEST, LEAST, LAG, LEAD,
// FIRST_VALUE, TRUNC — Oracle's date-polymorphic) are intentionally absent.
const BUILTIN_RESULT_TYPE: ReadonlyMap<string, OperandLiteralSqlType> = new Map([
  // ── numeric ──────────────────────────────────────────────────────────────
  // aggregates (COUNT is integer; SUM/AVG numeric)
  ['COUNT', 'integer'], ['SUM', 'numeric'], ['AVG', 'numeric'],
  // string length / size
  ['LENGTH', 'numeric'], ['CHAR_LENGTH', 'numeric'], ['CHARACTER_LENGTH', 'numeric'],
  ['LEN', 'numeric'], ['LENGTHB', 'numeric'], ['DATALENGTH', 'numeric'],
  ['OCTET_LENGTH', 'numeric'], ['BIT_LENGTH', 'numeric'],
  // position
  ['INSTR', 'numeric'], ['POSITION', 'numeric'], ['STRPOS', 'numeric'],
  ['CHARINDEX', 'numeric'], ['PATINDEX', 'numeric'], ['LOCATE', 'numeric'], ['ASCII', 'numeric'],
  ['UNICODE', 'numeric'], ['ORD', 'numeric'],
  // math
  ['ABS', 'numeric'], ['ROUND', 'numeric'], ['FLOOR', 'numeric'], ['CEIL', 'numeric'],
  ['CEILING', 'numeric'], ['MOD', 'numeric'], ['POWER', 'numeric'], ['POW', 'numeric'],
  ['SQRT', 'numeric'], ['EXP', 'numeric'], ['LN', 'numeric'], ['LOG', 'numeric'],
  ['LOG10', 'numeric'], ['LOG2', 'numeric'], ['SIGN', 'numeric'], ['DEGREES', 'numeric'],
  ['RADIANS', 'numeric'], ['PI', 'numeric'], ['RAND', 'numeric'], ['RANDOM', 'numeric'],
  ['TRUNCATE', 'numeric'],
  // date-part extraction
  ['YEAR', 'numeric'], ['MONTH', 'numeric'], ['DAY', 'numeric'], ['HOUR', 'numeric'],
  ['MINUTE', 'numeric'], ['SECOND', 'numeric'], ['DAYOFWEEK', 'numeric'], ['DAYOFMONTH', 'numeric'],
  ['DAYOFYEAR', 'numeric'], ['WEEK', 'numeric'], ['WEEKDAY', 'numeric'], ['QUARTER', 'numeric'],
  ['DATEPART', 'numeric'], ['DATEDIFF', 'numeric'], ['MONTHS_BETWEEN', 'numeric'], ['EXTRACT', 'numeric'],
  // window ranking
  ['ROW_NUMBER', 'numeric'], ['RANK', 'numeric'], ['DENSE_RANK', 'numeric'], ['NTILE', 'numeric'],
  // ── text ─────────────────────────────────────────────────────────────────
  ['UPPER', 'text'], ['LOWER', 'text'], ['UCASE', 'text'], ['LCASE', 'text'], ['INITCAP', 'text'],
  ['TRIM', 'text'], ['LTRIM', 'text'], ['RTRIM', 'text'], ['BTRIM', 'text'],
  ['SUBSTR', 'text'], ['SUBSTRING', 'text'], ['SUBSTRING_INDEX', 'text'], ['MID', 'text'],
  ['REPLACE', 'text'], ['TRANSLATE', 'text'], ['OVERLAY', 'text'],
  ['CONCAT', 'text'], ['CONCAT_WS', 'text'], ['GROUP_CONCAT', 'text'], ['STRING_AGG', 'text'],
  ['LISTAGG', 'text'], ['LPAD', 'text'], ['RPAD', 'text'], ['LEFT', 'text'], ['RIGHT', 'text'],
  ['REPEAT', 'text'], ['SPACE', 'text'], ['REVERSE', 'text'], ['TO_CHAR', 'text'], ['CHAR', 'text'],
  ['CHR', 'text'], ['FORMAT', 'text'], ['QUOTE', 'text'], ['HEX', 'text'], ['SOUNDEX', 'text'],
  ['MD5', 'text'], ['SHA1', 'text'], ['SHA2', 'text'],
]);

const CAST_RE = /^(?:TRY_)?CAST\s*\(.*\bAS\s+([A-Za-z][A-Za-z0-9_]*)/is;
const CONVERT_RE = /^(?:TRY_)?CONVERT\s*\(\s*([A-Za-z][A-Za-z0-9_]*)/i;
const PG_CAST_RE = /::\s*("?[A-Za-z][A-Za-z0-9_ ]*"?)\s*$/;
// Allows an optional schema/package qualifier (`app.fn`, `dbo.app.fn`) so a
// qualified user-defined call still reaches the routine resolver, which strips
// the qualifier. Built-ins are always bare, so the dotted path only ever
// matches UDFs.
const LEADING_CALL_RE = /^((?:[A-Za-z_][A-Za-z0-9_]*\.)*[A-Za-z_][A-Za-z0-9_]*)\s*\(/;

/**
 * Resolves a (possibly schema-qualified) routine NAME to its declared return
 * type string, e.g. `my_total` → `'numeric'`. Supplied by consumers that have a
 * schema snapshot in scope (the IntelliSense resolvers); omitted by the
 * schema-free parser. The implementation folds the name per engine — callers
 * pass the bare leading identifier of the call.
 */
export type ResolveRoutineReturnType = (functionName: string) => string | undefined;

/**
 * Infer the coarse result type of an expression operand's text. Returns
 * 'unknown' whenever the shape isn't one we can classify with confidence.
 *
 * When `resolveRoutineReturnType` is supplied, a single balanced call whose
 * name is NOT a curated built-in is looked up as a user-defined routine — its
 * declared return type (mapped through `columnSqlTypeFromDataType`) is used.
 * This is how `my_numeric_fn(x) ▮` prunes `LIKE` just like a numeric column.
 */
export function inferExpressionResultType(
  text: string,
  engine: SqlEngine,
  resolveRoutineReturnType?: ResolveRoutineReturnType,
): OperandLiteralSqlType {
  const trimmed = text.trim();
  if (!trimmed) return 'unknown';

  // CAST(expr AS <type>) / TRY_CAST(...) — the AS target drives the type.
  const cast = CAST_RE.exec(trimmed);
  if (cast) return columnSqlTypeFromDataType(cast[1]);

  // CONVERT(<type>, expr) — MSSQL puts the target type FIRST. (MySQL's
  // CONVERT(expr, type) / CONVERT(expr USING cs) differ, so gate on MSSQL.)
  if (engine === 'mssql') {
    const conv = CONVERT_RE.exec(trimmed);
    if (conv) return columnSqlTypeFromDataType(conv[1]);
  }

  // PostgreSQL `expr::type` cast — the trailing `::type` wins.
  const pgCast = PG_CAST_RE.exec(trimmed);
  if (pgCast) return columnSqlTypeFromDataType(pgCast[1].replace(/"/g, '').trim());

  // Single leading function call that spans the whole operand (balanced).
  const call = LEADING_CALL_RE.exec(trimmed);
  if (call && isSingleBalancedCall(trimmed, call[0].length - 1)) {
    const fnType = BUILTIN_RESULT_TYPE.get(call[1].toUpperCase());
    if (fnType) return fnType;
    // Not a built-in → try the user-defined routine's declared return type.
    if (resolveRoutineReturnType) {
      const ret = resolveRoutineReturnType(call[1]);
      if (ret) {
        const mapped = columnSqlTypeFromDataType(ret);
        if (mapped !== 'unknown') return mapped;
      }
    }
  }

  // Arithmetic / concatenation at the top level (outside parens + strings).
  return inferOperatorResultType(trimmed, engine);
}

/**
 * True when the `(` at `openIdx` closes exactly at the end of `text` — i.e.
 * the whole operand is one `name( … )` call, not `f(a) + g(b)`. String
 * literals are skipped so a `)` inside a quoted argument doesn't fool us.
 */
function isSingleBalancedCall(text: string, openIdx: number): boolean {
  let depth = 0;
  let inStr: string | null = null;
  for (let i = openIdx; i < text.length; i += 1) {
    const ch = text[i]!;
    if (inStr) {
      if (ch === inStr) inStr = null;
      continue;
    }
    if (ch === "'" || ch === '"' || ch === '`') { inStr = ch; continue; }
    if (ch === '(') depth += 1;
    else if (ch === ')') {
      depth -= 1;
      if (depth === 0) return i === text.length - 1;
    }
  }
  return false;
}

/**
 * Top-level binary-operator inference. Only the unambiguous cases:
 *   - `*` / `/` / `%` anywhere at top level  → numeric (no date/string overload)
 *   - `||` at top level → text, EXCEPT MySQL where `||` is logical-OR by default
 * `+` / `-` are deliberately NOT inferred — they're overloaded for date math
 * and (MSSQL) string concatenation.
 */
function inferOperatorResultType(text: string, engine: SqlEngine): OperandLiteralSqlType {
  let depth = 0;
  let inStr: string | null = null;
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i]!;
    if (inStr) {
      if (ch === inStr) inStr = null;
      continue;
    }
    if (ch === "'" || ch === '"' || ch === '`') { inStr = ch; continue; }
    if (ch === '(') { depth += 1; continue; }
    if (ch === ')') { depth -= 1; continue; }
    if (depth !== 0) continue;
    if (ch === '*' || ch === '/' || ch === '%') return 'numeric';
    if (ch === '|' && text[i + 1] === '|' && engine !== 'mysql') return 'text';
  }
  return 'unknown';
}
